'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Arrow from 'images/icons/arrow.svg';
import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

const BUTTON_VISIBLE_SCROLL_VALUE = 300;

// top = HEADER_HEIGHT (80px) + sizes.8 (2rem), as in atoms/ScrollToTop.
const wrapperClass = css({
  position: 'fixed',
  top: 'calc(80px + 2rem)',
  left: '50%',
  transition: 'transform 0.2s linear',
  width: '48px',
  height: '32px',
  zIndex: 4,
});
// Icon-only control (not a DS button): the `ghost` recipe base + its own violet pill.
const buttonClass = css({
  width: '100%',
  height: '100%',
  px: 0,
  borderRadius: '100px',
  backgroundColor: 'violet.500',
  _active: { backgroundColor: 'violet.600' },
  _hover: { backgroundColor: 'violet.600' },
  '& svg': { fill: 'white', width: '1em', height: '1em' },
});

/** atoms/ScrollToTop without Chakra: appears on scroll-up past 300px, smooth-scrolls to the top. */
export const ScrollToTop = () => {
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const rafIdRef = useRef<number | null>(null);
  // Previous scroll position in a ref: no mount-time scrollY read (forced layout), see the original.
  const prevScrollYRef = useRef<number | null>(null);

  const toggleButtonVisible = useCallback(() => {
    if (rafIdRef.current !== null) return;

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const scrollTopValue = window.scrollY;
      const prevScrollY = prevScrollYRef.current;

      if (scrollTopValue > BUTTON_VISIBLE_SCROLL_VALUE && prevScrollY !== null && prevScrollY > scrollTopValue) {
        setIsButtonVisible(true);
      } else if (scrollTopValue <= BUTTON_VISIBLE_SCROLL_VALUE) {
        setIsButtonVisible(false);
      }

      prevScrollYRef.current = scrollTopValue;
    });
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', toggleButtonVisible, { passive: true });

    return () => {
      document.removeEventListener('scroll', toggleButtonVisible);
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, [toggleButtonVisible]);

  return (
    <div className={wrapperClass} style={{ transform: `translateX(-50%) scale(${isButtonVisible ? '1' : '0'})` }}>
      <button
        type="button"
        aria-label="Scroll to top"
        aria-hidden={!isButtonVisible}
        tabIndex={isButtonVisible ? 0 : -1}
        onClick={() => scrollTo({ top: 0, behavior: 'smooth' })}
        className={cx(button({ variant: 'ghost', size: 'sm' }), buttonClass)}
      >
        <Arrow aria-hidden focusable={false} />
      </button>
    </div>
  );
};
